/**
 * @fileoverview View component of the RichTextEditor
 */

import React, {
  useCallback,
  useMemo,
  Fragment,
  useEffect,
  useState,
} from 'react'
import isHotkey from 'is-hotkey'
import { Editable, withReact, useSlate, Slate } from 'slate-react'
import {
  Editor,
  Transforms,
  createEditor,
  Element as SlateElement,
} from 'slate'
import { withHistory } from 'slate-history'
import { Button, Toolbar } from '@material-ui/core'
import {
  FormatUnderlined,
  FormatBold,
  FormatItalic,
  Code,
  LooksOne,
  LooksTwo,
  FormatQuote,
  FormatListNumbered,
  FormatListBulleted,
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight,
  FormatAlignJustify,
} from '@material-ui/icons'
import { makeStyles } from '@material-ui/styles'

import { emailPlaceholderRegex } from 'Utils/Regexp'
import { styles } from './styles'
import EmojiPicker from '../EmojiPicker'
import { findUrlsInText } from '../../shared/libraries/find'

const HOTKEYS = {
  'mod+b': 'bold',
  'mod+i': 'italic',
  'mod+u': 'underline',
  'mod+`': 'code',
}

const LIST_TYPES = ['numbered-list', 'bulleted-list']
const TEXT_ALIGN_TYPES = ['left', 'center', 'right', 'justify']

const formatUrlToValid = string => {
  try {
    new URL(string)
    return string
  } catch (_) {
    return `http://${string}`
  }
}

const findPlaceholdersInText = text => {
  const matches = text.match(emailPlaceholderRegex)
  return matches ? matches.map(m => [m.trim(), text.indexOf(m.trim())]) : []
}

const decorator = ([node, path]) => {
  const nodeText = node.text

  if (!nodeText) return []

  const placeholders = findPlaceholdersInText(nodeText)
  const urls = findUrlsInText(nodeText)

  const placeholderItems = placeholders.map(([placeholder, index]) => {
    return {
      anchor: {
        path,
        offset: index,
      },
      focus: {
        path,
        offset: index + placeholder.length,
      },
      decoration: 'placeholder',
    }
  })

  const urlsItems = urls.map(([url, index]) => {
    return {
      anchor: {
        path,
        offset: index,
      },
      focus: {
        path,
        offset: index + url.length,
      },
      decoration: 'link',
    }
  })
  return [...placeholderItems, ...urlsItems]
}

/**
 * A rich text editor component
 *
 * @param {boolean} props.markTools Show bold, italic and underline buttons
 * @param {boolean} props.alignTools Show left, center, right and justify buttons
 * @param {boolean} props.listTools Show numbered and bulleted list buttons
 * @param {boolean} props.headingTools Show heading buttons
 * @param {boolean} props.blockTools Show code and block quote buttons
 * @param {boolean} props.emojiTools Show emoji picker
 * @param {boolean} props.readOnly Set if the editor is read only
 * @param {string} props.index The index of the editor
 * @param {array} props.value The value of the editor
 * @param {string} props.textToInsert A stateful value to insert text
 * @param {function} props.onChange The function to call when the editor changes
 * @param {string} props.fontSize The font size of the editor
 * @param {string} props.rows The number of rows of the editor
 *
 * @returns {JSX.Element}
 */

const RichTextEditor = ({
  markTools = false,
  alignTools = false,
  listTools = false,
  headingTools = false,
  blockTools = false,
  emojiTools = false,
  extraTools = null,
  readOnly = false,
  disableBorder = false,
  index,
  value = defaultInitialValue,
  textToInsert = null,
  onChange = value => {},
  onClick = () => {},
  fontSize = '14px',
  rows = '12',
  currentValueLength,
  maxLength,
}) => {
  const useStyles = makeStyles(theme =>
    styles({ fontSize, readOnly: readOnly || disableBorder, rows }),
  )
  const renderElement = useCallback(props => <Element {...props} />, [])
  const renderLeaf = useCallback(props => <Leaf {...props} />, [])
  const editor = useMemo(() => withHistory(withReact(createEditor())), [])
  const [emojiPicked, setEmojiPicked] = useState(null)
  const insertedText = useMemo(() => textToInsert, [textToInsert])
  const classes = useStyles()
  const isToolbarVisible =
    markTools || alignTools || listTools || headingTools || blockTools
  const isCurrentValueLengthNumber =
    typeof currentValueLength === 'number' && isFinite(currentValueLength)
  const isMaxLengthNumber = typeof maxLength === 'number' && isFinite(maxLength)

  const maxLengthReached = useMemo(() => {
    if (isCurrentValueLengthNumber && isMaxLengthNumber) {
      return currentValueLength >= maxLength
    } else {
      return false
    }
  }, [currentValueLength, maxLength])

  useEffect(() => {
    if (insertedText) {
      editor.insertText(insertedText)
    }
  }, [insertedText])

  useEffect(() => {
    if (emojiPicked) {
      editor.insertText(emojiPicked)
      setEmojiPicked(null)
    }
  }, [emojiPicked])

  const handleEmojiClick = (event, emojiObject) => {
    setEmojiPicked(emojiObject.emoji)
  }

  return (
    <div className={classes.root}>
      <Slate
        key={index + '-slate'}
        editor={editor}
        value={value}
        onChange={value => {
          const isAstChange = editor.operations.some(
            op => 'set_selection' !== op.type,
          )
          if (isAstChange) {
            onChange(value)
          }
        }}
      >
        {isToolbarVisible && (
          <Toolbar key={index + '-toolbar'} className={classes.toolbar}>
            {markTools && (
              <Fragment>
                <MarkButton className={classes.button} format="bold">
                  <FormatBold />
                </MarkButton>
                <MarkButton className={classes.button} format="italic">
                  <FormatItalic />
                </MarkButton>
                <MarkButton className={classes.button} format="underline">
                  <FormatUnderlined />
                </MarkButton>
              </Fragment>
            )}
            {blockTools && (
              <Fragment>
                <MarkButton className={classes.button} format="code">
                  <Code />
                </MarkButton>
                <BlockButton className={classes.button} format="block-quote">
                  <FormatQuote />
                </BlockButton>
              </Fragment>
            )}
            {headingTools && (
              <Fragment>
                <BlockButton className={classes.button} format="heading-one">
                  <LooksOne />
                </BlockButton>
                <BlockButton className={classes.button} format="heading-two">
                  <LooksTwo />
                </BlockButton>
              </Fragment>
            )}
            {listTools && (
              <Fragment>
                <BlockButton className={classes.button} format="numbered-list">
                  <FormatListNumbered />
                </BlockButton>
                <BlockButton className={classes.button} format="bulleted-list">
                  <FormatListBulleted />
                </BlockButton>
              </Fragment>
            )}
            {alignTools && (
              <Fragment>
                <BlockButton className={classes.button} format="left">
                  <FormatAlignLeft />
                </BlockButton>
                <BlockButton className={classes.button} format="center">
                  <FormatAlignCenter />
                </BlockButton>
                <BlockButton className={classes.button} format="right">
                  <FormatAlignRight />
                </BlockButton>
                <BlockButton className={classes.button} format="justify">
                  <FormatAlignJustify />
                </BlockButton>
              </Fragment>
            )}
            {emojiTools && (
              <Fragment>
                <EmojiPicker onEmojiClick={handleEmojiClick} />
              </Fragment>
            )}
            {extraTools}
          </Toolbar>
        )}
        <Editable
          key={index + '-editable'}
          className={classes.editable}
          renderElement={renderElement}
          renderLeaf={renderLeaf}
          placeholder="Enter text…"
          readOnly={readOnly}
          spellCheck
          autoFocus
          onClick={onClick}
          onPaste={e => {
            const pastedText = e.clipboardData.getData('Text')
            if (
              isCurrentValueLengthNumber &&
              isMaxLengthNumber &&
              currentValueLength + pastedText.length > maxLength
            ) {
              e.preventDefault()
              e.stopPropagation()
            }
          }}
          onKeyDown={event => {
            if (maxLengthReached && event.key !== 'Backspace') {
              event.preventDefault()
            }
            for (const hotkey in HOTKEYS) {
              if (isHotkey(hotkey, event)) {
                event.preventDefault()
                const mark = HOTKEYS[hotkey]
                toggleMark(editor, mark)
              }
            }
          }}
          decorate={decorator}
        />
      </Slate>
    </div>
  )
}

const toggleBlock = (editor, format) => {
  const isActive = isBlockActive(
    editor,
    format,
    TEXT_ALIGN_TYPES.includes(format) ? 'align' : 'type',
  )
  const isList = LIST_TYPES.includes(format)

  Transforms.unwrapNodes(editor, {
    match: n =>
      !Editor.isEditor(n) &&
      SlateElement.isElement(n) &&
      LIST_TYPES.includes(n.type) &&
      !TEXT_ALIGN_TYPES.includes(format),
    split: true,
  })
  let newProperties
  if (TEXT_ALIGN_TYPES.includes(format)) {
    newProperties = {
      align: isActive ? undefined : format,
    }
  } else {
    newProperties = {
      type: isActive ? 'paragraph' : isList ? 'list-item' : format,
    }
  }
  Transforms.setNodes(editor, newProperties)

  if (!isActive && isList) {
    const block = { type: format, children: [] }
    Transforms.wrapNodes(editor, block)
  }
}

const toggleMark = (editor, format) => {
  const isActive = isMarkActive(editor, format)

  if (isActive) {
    Editor.removeMark(editor, format)
  } else {
    Editor.addMark(editor, format, true)
  }
}

const isBlockActive = (editor, format, blockType = 'type') => {
  const { selection } = editor
  if (!selection) return false

  const [match] = Array.from(
    Editor.nodes(editor, {
      at: Editor.unhangRange(editor, selection),
      match: n =>
        !Editor.isEditor(n) &&
        SlateElement.isElement(n) &&
        n[blockType] === format,
    }),
  )

  return !!match
}

const isMarkActive = (editor, format) => {
  const marks = Editor.marks(editor)
  return marks ? marks[format] === true : false
}

const Element = ({ attributes, children, element }) => {
  const style = { textAlign: element.align }
  switch (element.type) {
    case 'block-quote':
      return (
        <blockquote style={style} {...attributes}>
          {children}
        </blockquote>
      )
    case 'bulleted-list':
      return (
        <ul style={style} {...attributes}>
          {children}
        </ul>
      )
    case 'heading-one':
      return (
        <h1 style={style} {...attributes}>
          {children}
        </h1>
      )
    case 'heading-two':
      return (
        <h2 style={style} {...attributes}>
          {children}
        </h2>
      )
    case 'list-item':
      return (
        <li style={style} {...attributes}>
          {children}
        </li>
      )
    case 'numbered-list':
      return (
        <ol style={style} {...attributes}>
          {children}
        </ol>
      )
    default:
      return (
        <p style={style} {...attributes}>
          {children}
        </p>
      )
  }
}

const Leaf = ({ attributes, children, leaf }) => {
  if (leaf.bold) {
    children = <strong>{children}</strong>
  }

  if (leaf.code) {
    children = <code>{children}</code>
  }

  if (leaf.italic) {
    children = <em>{children}</em>
  }

  if (leaf.underline) {
    children = <u>{children}</u>
  }

  if (leaf.decoration === 'placeholder') {
    children = <span style={{ backgroundColor: '#E2E2FC' }}>{children}</span>
  }

  if (leaf.decoration === 'link') {
    children = (
      <a
        style={{ cursor: 'pointer' }}
        href={leaf.text}
        onClick={() => {
          window.open(
            formatUrlToValid(leaf.text),
            '_blank',
            'noopener,noreferrer',
          )
        }}
      >
        {children}
      </a>
    )
  }

  return <span {...attributes}>{children}</span>
}

const BlockButton = ({ format, children, className }) => {
  const editor = useSlate()
  const isActive = isBlockActive(
    editor,
    format,
    TEXT_ALIGN_TYPES.includes(format) ? 'align' : 'type',
  )
  const childrenWithProps = React.Children.map(children, child =>
    React.cloneElement(child, {
      fontSize: 'small',
      active: isActive ? 'action' : 'disabled',
    }),
  )
  return (
    <Button
      className={className}
      active={isActive}
      onMouseDown={event => {
        event.preventDefault()
        toggleBlock(editor, format)
      }}
    >
      {childrenWithProps}
    </Button>
  )
}

const MarkButton = ({ format, children, className }) => {
  const editor = useSlate()
  const isActive = isMarkActive(editor, format)
  const childrenWithProps = React.Children.map(children, child =>
    React.cloneElement(child, {
      fontSize: 'small',
      color: isActive ? 'action' : 'disabled',
    }),
  )
  return (
    <Button
      className={className}
      active={isActive}
      onMouseDown={event => {
        event.preventDefault()
        toggleMark(editor, format)
      }}
    >
      {childrenWithProps}
    </Button>
  )
}

const defaultInitialValue = [
  {
    type: 'paragraph',
    align: 'left',
    children: [{ text: '' }],
  },
]

export default RichTextEditor
